import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import validate from 'validate.js';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import {
	Button, TextField, Typography, Grid, Select, Card, CardHeader,
	CardContent, Divider, Accordion, AccordionSummary, AccordionDetails,
	FormGroup, Switch
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import useRouter from 'utils/useRouter';
import { SelectField, DatePicker } from 'components';
import DateTimePicker from 'components/DatePicker/DateTimePicker.js';
import { SetFormState } from '../../../../../actions/PacketActions';
import PacketMocMtcDataTable from './components/PacketMocMtcDataTable';
import PacketComments from './components/PacketComments';

const schema = {
	firstname: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	lastname: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	email: {
		presence: { allowEmpty: false, message: 'is required' },
		email: true,
	},
	roleId: {
		presence: { allowEmpty: false, message: 'is required' },
	},
};

const useStyles = makeStyles((theme) => ({
	root: {},
	fields: {
		margin: theme.spacing(-1),
		display: 'flex',
		flexWrap: 'wrap',
		'& > *': {
			flexGrow: 1,
			margin: theme.spacing(1),
		},
	},
	submitButton: {
		marginTop: theme.spacing(2),
		width: '100%',
	},
	divider: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2),
	}
}));

const PacketEditFailureDialogForm = (props) => {
	const { className, tableId, ...rest } = props;

	const classes = useStyles();
	const router = useRouter();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.packetReducer.formState);
	const userId = useSelector(state => state.session.user.id);
	const role = useSelector(state => state.session.user.role);

	const [hideFields, setHideFields] = React.useState(false);
	const failure = useSelector((state) => state.packetReducer.failure);
	const [expectedSolutionDate, setExpectedDate] = useState(new Date());
	const [actualSolutionDate, setActualDate] = useState(new Date());

	// const [stateValid, setStateValid] = React.useState(true);

	useMemo(() => {
		const errors = validate(formState.values, schema);

		setFormState({
			...formState,
			isValid: !errors,
			errors: errors || {},
		});

		// if (hasOperatorIdsChanged) {
		//     dispatch(GetOperatorProjects(formState.values.operatorIds || []));
		// }
		if (role === 'Client') {
			setHideFields(true);
		} else {
			setHideFields(false);
		}

		// if (formState.values.valid) {
		// 	setStateValid(true);
		// } else {
		// 	setStateValid(false);
		// }
	}, [formState.values]);

	const _handleChange = (event) => {
		event.persist();
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value,
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			},
		});
	};

	const _handleDateChange = (value, name) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[name]:
					value,
			},
			touched: {
				...formState.touched,
				[name]: true,
			},
		});
	};

	// const handleChangeValid = (event) => {
	// 	setStateValid(!stateValid);

	// 	setFormState({
	// 		...formState,
	// 		values: {
	// 			...formState.values,
	// 			valid: event.target.checked
	// 		}
	// 	});
	// };

	const _hasError = (field) => (!!(formState.touched[field] && formState.errors[field]));
	return (
		<div className={classes.fields}>
			{/* New row */}
			<Grid container spacing={4}>
				<Grid item md={12} xs={12}>
					<Card>
						<Grid container spacing={1}>
							<Grid item md={10} xs={10}>
								<CardHeader
									title={`Failure: ${formState.values.failureId || ''}`}
								/>
							</Grid>
							{/* <Grid item md={2} xs={2} container justify="flex-end">
								<div hidden={hideFields}>
									<FormGroup>
										<Typography component="div">
											<Grid component="label" container alignItems="center" spacing={1}>
												<Grid item>Invalid</Grid>
												<Grid item>
													<Switch
														checked={stateValid}
														onChange={handleChangeValid}
														color="primary"
														name="checkedValid"
														inputProps={{ 'aria-label': 'primary checkbox' }}
													/>
												</Grid>
												<Grid item>Valid&nbsp;</Grid>
											</Grid>
										</Typography>
									</FormGroup>
								</div>
							</Grid> */}
						</Grid>
						<Divider />
						<CardContent>
							<Grid container spacing={4}>
								<Grid item md={1} xs={1} />
								<Grid item md={2} xs={2}>
									<TextField
										id="failureSourceHeader"
										label="Source"
										name="failureSource"
										InputProps={{
											readOnly: true,
										}}
										variant="outlined"
										error={_hasError('failureSource')}
										fullWidth
										helperText={
											_hasError('failureSource') ? formState.errors.failureSource[0] : null
										}
										value={formState.values.failureSource || ''}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="routeTypeHeader"
										label="Route Type"
										name="routeType"
										InputProps={{
											readOnly: true,
										}}
										variant="outlined"
										error={_hasError('routeType')}
										fullWidth
										helperText={
											_hasError('routeType') ? formState.errors.routeType[0] : null
										}
										value={formState.values.routeType || ''}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="routeNameHeader"
										label="Route Name"
										name="routeName"
										InputProps={{
											readOnly: true,
										}}
										variant="outlined"
										error={_hasError('routeName')}
										fullWidth
										helperText={
											_hasError('routeName') ? formState.errors.routeName[0] : null
										}
										value={formState.values.routeName || ''}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="mobilityScenarioHeader"
										label="Mobility scenario"
										name="mobilityScenario"
										InputProps={{
											readOnly: true,
										}}
										variant="outlined"
										error={_hasError('mobilityScenario')}
										fullWidth
										helperText={
											_hasError('mobilityScenario') ? formState.errors.mobilityScenario[0] : null
										}
										value={formState.values.mobilityScenario || ''}
									/>
								</Grid>
								<Grid item md={2} xs={2}>
									<TextField
										id="testTypeHeader"
										label="Test type"
										name="testType"
										InputProps={{
											readOnly: true,
										}}
										variant="outlined"
										error={_hasError('testType')}
										fullWidth
										helperText={
											_hasError('testType') ? formState.errors.testType[0] : null
										}
										value={formState.values.testType || ''}
									/>
								</Grid>
								<Grid item md={1} xs={1} />
							</Grid>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
			{/* New row */}
			<Grid container spacing={4}>
				<Grid item md={6} xs={6}>
					<Card>
						<CardHeader title="General" />
						<Divider />
						<CardContent>
							{/* <Grid container spacing={4}>
								<Grid item md={3} xs={3}>
									<TextField
										id="csr"
										label="CSR"
										name="csr"
										InputProps={{
											readOnly: true,
										}}
										// variant="outlined"
										error={_hasError('csr')}
										fullWidth
										helperText={
											_hasError('csr') ? formState.errors.csr[0] : null
										}
										value={formState.values.csr || ''}
									/>
								</Grid>
								<Grid item md={3} xs={3}>
									<TextField
										id="reallocatedCount"
										label="Times reallocated"
										name="reallocatedCount"
										InputProps={{
											readOnly: true,
										}}
										// variant="outlined"
										error={_hasError('reallocatedCount')}
										fullWidth
										helperText={
											_hasError('reallocatedCount') ? formState.errors.reallocatedCount[0] : null
										}
										value={formState.values.reallocatedCount || ''}
									/>
								</Grid>
							</Grid>
							<Divider className={classes.divider} variant="middle" /> */}
							<Grid container spacing={4}>
								<Grid item md={3} xs={3}>
									<TextField
										id="initialClassification"
										label="Initial classification"
										name="initialClassification"
										InputProps={{
											readOnly: true,
										}}
										variant="outlined"
										error={_hasError('initialClassification')}
										fullWidth
										helperText={
											_hasError('initialClassification') ? formState.errors.initialClassification[0] : null
										}
										value={formState.values.initialClassification || ''}
									/>
								</Grid>
								<Grid item md={3} xs={3}>
									<TextField
										id="date"
										label="Timestamp"
										name="date"
										InputProps={{
											readOnly: true,
										}}
										variant="outlined"
										error={_hasError('date')}
										fullWidth
										helperText={
											_hasError('date') ? formState.errors.date[0] : null
										}
										value={formState.values.date || ''}
									/>
								</Grid>
								<Grid item md={3} xs={3}>
									<TextField
										id="week"
										label="Week"
										name="week"
										InputProps={{
											readOnly: true,
										}}
										variant="outlined"
										error={_hasError('week')}
										fullWidth
										helperText={
											_hasError('week') ? formState.errors.week[0] : null
										}
										value={formState.values.week || ''}
									/>
								</Grid>
								<Grid item md={3} xs={3}>
									<TextField
										id="incNumber"
										label="Incident Number"
										name="incNumber"
										disabled={(failure && failure.incNumber) == null}
										InputProps={{
											readOnly: true,
										}}
										variant="outlined"
										error={_hasError('incNumber')}
										fullWidth
										helperText={
											_hasError('incNumber') ? failure.incNumber : null
										}
										// onChange={_handleChange}
										value={(failure && failure.incNumber) || ''}
									/>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
				<Grid item md={6} xs={6}>
					<Card>
						<CardHeader title="" />
						<Divider />
						<CardContent>
							<Grid container spacing={4}>
								<Grid item md={12} xs={12}>
									<PacketMocMtcDataTable data={(failure && failure.trackerPacketMocMtcDto) || []} />
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
			{/* New row */}
			<Grid container spacing={4}>
				<Grid item md={6} xs={6} hidden={hideFields}>
					<PacketComments tableId={tableId} comments={(failure && failure.trackerPacketCommentsDto) || []} />
				</Grid>
				<Grid item md={6} xs={6}>
					<Card>
						<CardHeader title="Failure correction" />
						<Divider />
						<CardContent>
							<Grid item md={12} xs={12}>
								<Accordion defaultExpanded>
									<AccordionSummary
										expandIcon={<ExpandMoreIcon />}
										aria-controls="panel1a-content"
										id="panel1a-header"
									>
										<Typography>Levels</Typography>
									</AccordionSummary>
									<AccordionDetails>
										<Grid container direction="vertical" spacing={4}>
											<Grid item md={6} xs={6}>
												<TextField
													id="cfn1Level"
													label="1st Level"
													defaultValue=""
													InputProps={{
														readOnly: true,
													}}
													variant="outlined"
													fullWidth
												/>
											</Grid>
											<Grid item md={6} xs={6}>
												<SelectField
													id="cfn2Level"
													name="cfn2Level"
													hintText="2nd Level"
													defaultValue=""
													menuItems={(failure && failure.cfn2LevelList) || []}
													value={formState.values.cfn2Level || ''}
													onChange={_handleChange}
												/>
											</Grid>
											<Grid item md={6} xs={6}>
												<SelectField
													id="cfn3Level"
													name="cfn3Level"
													hintText="3rd Level"
													defaultValue=""
													menuItems={(failure && failure.cfn3LevelList) || []}
													value={formState.values.cfn3Level || ''}
													onChange={_handleChange}
												/>
											</Grid>
											<Grid item md={6} xs={6}>
												<SelectField
													id="reallocatedLevel"
													name="reallocatedLevel"
													hintText="Reallocated Level"
													defaultValue=""
													menuItems={[]}
													onChange={_handleChange}
												/>
											</Grid>
											<Grid item md={6} xs={6}>
												<SelectField
													id="finalClassification"
													name="finalClassification"
													hintText="Final Classification"
													defaultValue=""
													menuItems={[]}
													readOnly
												/>
											</Grid>
										</Grid>
									</AccordionDetails>
								</Accordion>
								<Accordion defaultExpanded hidden={hideFields}>
									<AccordionSummary
										expandIcon={<ExpandMoreIcon />}
										aria-controls="panel1a-content"
										id="panel1a-header"
									>
										<Typography>Solution</Typography>
									</AccordionSummary>
									<AccordionDetails>
										<Grid container direction="vertical" spacing={4}>
											<Grid item md={6} xs={6}>
												<SelectField
													id="status"
													name="status"
													hintText="Status"
													defaultValue=""
													menuItems={(failure && failure.statusList) || []}
													value={formState.values.status || ''}
													onChange={_handleChange}
												/>
											</Grid>
											<Grid item md={6} xs={6}>
												<SelectField
													id="solution"
													name="solution"
													hintText="Solution"
													defaultValue=""
													menuItems={(failure && failure.solutionList) || []}
													value={formState.values.solution || ''}
													onChange={_handleChange}
												/>
											</Grid>
											<Grid item md={6} xs={6}>
												<SelectField
													id="solutionOwner"
													hintText="Solution Owner"
													defaultValue=""
													menuItems={(failure && failure.solutionOwnerList) || []}
													value={formState.values.solutionOwner || ''}
													readOnly
												/>
											</Grid>
											<Grid item md={6} xs={6}>
												<TextField
													id="owner"
													label="Owner"
													defaultValue=""
													InputProps={{
														readOnly: true,
													}}
													variant="outlined"
													fullWidth
												/>
											</Grid>
											<Grid item md={12} xs={12}>
												<TextField
													id="solutionDescription"
													name="solutionDescription"
													label="Description"
													defaultValue=""
													value={formState.values.solutionDescription || ''}
													fullWidth
													onChange={_handleChange}
												/>
											</Grid>
											<Grid item md={6} xs={6}>
												<DateTimePicker
													id="expectedSolutionDate"
													name="expectedSolutionDate"
													label="Expected Solution Date"
													style={{ width: '100%' }}
													value={formState.values.expectedSolutionDate || ''}
													onChange={(value) => _handleDateChange(value, 'expectedSolutionDate')}
												/>
											</Grid>
											<Grid item md={6} xs={6}>
												<DateTimePicker
													id="actualSolutionDate"
													name="actualSolutionDate"
													label="Actual Solution Date"
													style={{ width: '100%' }}
													value={formState.values.actualSolutionDate || ''}
													onChange={(value) => _handleDateChange(value, 'actualSolutionDate')}
													minutesStep={1}
												/>
											</Grid>
										</Grid>
									</AccordionDetails>
								</Accordion>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		</div>
	);
};

PacketEditFailureDialogForm.propTypes = {
	className: PropTypes.string,
	tableId: PropTypes.string
};

export default PacketEditFailureDialogForm;
